import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet } from '../api/client.js';
import { useAuth } from '../contexts/AuthContext.jsx';

/**
 * Topbar org chip — ADR-0020.
 *
 * Single-org users: renders as a plain pill (no dropdown chrome). Same
 * affordance as before the multi-org switcher landed, so nothing changes
 * visually until the user is actually a member of more than one org.
 *
 * Multi-org users: pill becomes a click-to-open menu listing every org
 * the user belongs to. Picking one calls POST /me/switch-org, swaps the
 * JWT, and clears the react-query cache (the old data was scoped to a
 * different org and is now invalid).
 *
 * Open: click. Close: click outside, press Escape, pick an org.
 */
export default function OrgSwitcher() {
  const { organization, switchOrg } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [err, setErr] = useState(null);
  const wrapRef = useRef(null);

  const orgsQ = useQuery({
    queryKey: ['me', 'orgs'],
    queryFn: ({ signal }) => apiGet('/me/orgs', { signal }),
    // Cheap query, doesn't need to refetch on every focus — orgs list
    // changes only when the user is invited to a new one, which is
    // rare and triggered out-of-band (email link).
    staleTime: 60_000,
  });

  // Click-outside / Escape to close
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!organization) return null;

  const orgs = orgsQ.data?.organizations || [];
  const hasMultiple = orgs.length > 1;

  // Single-org or still loading: render the plain pill.
  if (!hasMultiple) {
    return (
      <span className="org-pill" title={organization.name}>
        <span className="org-pill-name">{organization.name}</span>
      </span>
    );
  }

  async function pickOrg(orgId) {
    if (orgId === organization.id) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    setErr(null);
    try {
      await switchOrg(orgId);
      // Throw away cached data scoped to the old org. Easier and safer
      // than per-key invalidation; the perceived "loading" cost is
      // 1-2 short re-fetches.
      queryClient.clear();
      setOpen(false);
    } catch (e) {
      setErr(e.body?.error || e.message || 'Switch failed');
    } finally {
      setSwitching(false);
    }
  }

  return (
    <div className="org-switcher-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`org-pill org-pill-button ${open ? 'open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        disabled={switching}
      >
        <span className="org-pill-name">{organization.name}</span>
        <span className="user-link-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="user-menu-dropdown org-switcher-dropdown" role="menu">
          <div className="user-menu-header">
            <div className="muted small">Switch organization</div>
          </div>
          {orgs.map((o) => (
            <button
              key={o.id}
              type="button"
              className={`user-menu-item org-switcher-item ${o.is_current ? 'is-current' : ''}`}
              onClick={() => pickOrg(o.id)}
              disabled={switching}
              role="menuitem"
            >
              <span className="user-menu-item-icon" aria-hidden="true">{o.is_current ? '✓' : '·'}</span>
              <span className="org-switcher-name">{o.name}</span>
              <span className="user-menu-role">{o.role}</span>
            </button>
          ))}
          {err && <div className="user-menu-footer pillar-fire-msg err">{err}</div>}
        </div>
      )}
    </div>
  );
}
